import { useEntity } from 'utils/hooks/api';
import ExploreDataSourceTable from 'components/DataVis/DataTable/ExploreDataSourceTable';
import GoogleExploreDrawerContent from './GoogleExploreDrawerContent';
import {
    GOOGLE_ROW_HEIGHT,
    getGoogleDownloadOptions,
    googleExploreBlocker,
    googleNoDataMessage,
} from './googleExploreTable';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import NoDataSourceMarketCoverage from 'components/NoDataSourceMarketCoverage';
import {
    DATA_SOURCES,
    TIKTOK_COUNTRIES,
    brandHealthStats,
    brandHealthStatsNonTikTok,
    categoryHealthStats,
    categoryHealthStatsNonTikTok,
} from '@/config/constants';
import {
    FLAVORS_METADATA_METRIC,
    FLAVORS_SORT_OPTIONS,
} from 'components/FlavorFilters/utils';
import {
    GOOGLE_METADATA_BY_KEY,
    GOOGLE_SORT_OPTIONS,
    TIKTOK_SORT_OPTIONS,
} from 'utils/metrics';
import {
    generatePath,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import { mixpanelTrack } from 'utils/app';
import { useEffect } from 'react';
import {
    useAbortController,
    useCurrentCountry,
    useGoogleData,
} from 'utils/hooks';
import { DataSourceBreadCrumbs } from 'components';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import Drawer from 'components/Drawer';
import GooglePresetFilterChips from 'components/FilterChips/GooglePresetFilterChips';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import Stack from 'components/Stack';
import { healthStatFilterOptions } from 'utils/filters';
import styled from 'styled-components';
import useCountryParam from 'utils/hooks/useCountryParam';
import useTikTokWeeks from 'utils/hooks/useTikTokWeeks';

function getMetrics({ isNotTikTokMarket, trendType }) {
    const metrics = [
        [FLAVORS_METADATA_METRIC, true],
        [GOOGLE_METADATA_BY_KEY.volume, true],
        [GOOGLE_METADATA_BY_KEY.absoluteGrowth, true],
        [GOOGLE_METADATA_BY_KEY.YoY, true],
        [GOOGLE_METADATA_BY_KEY.QoQ_increase, false],
        [GOOGLE_METADATA_BY_KEY.QoQ, false],
        [GOOGLE_METADATA_BY_KEY.MoM_increase, false],
        [GOOGLE_METADATA_BY_KEY.MoM, false],
    ];

    if (trendType === 'brands') {
        metrics.unshift([GOOGLE_METADATA_BY_KEY.price_segmentation, true]);
    } else {
        metrics.push([GOOGLE_METADATA_BY_KEY.brandsVolume, true]);
    }

    if (isNotTikTokMarket) {
        metrics.push([GOOGLE_METADATA_BY_KEY.predictedYoY, true]);
    }

    return metrics;
}

function getHealthStatKeys({ isNotTikTokMarket, trendType }) {
    const healthStatKeys =
        trendType === 'cat_fives'
            ? isNotTikTokMarket
                ? categoryHealthStats
                : categoryHealthStatsNonTikTok
            : isNotTikTokMarket
              ? brandHealthStats
              : brandHealthStatsNonTikTok;
    return healthStatKeys;
}

function getHealthStatFilterOptions({ isNotTikTokMarket, trendType }) {
    const healthStatKeys = getHealthStatKeys({ isNotTikTokMarket, trendType });
    return healthStatFilterOptions.filter(({ label }) =>
        healthStatKeys.includes(label),
    );
}

export function Category({ categoryQuery }) {
    const { type, categoryId: rootCategoryId } = useParams();
    const trendType = type === 'trends' ? 'cat_fives' : type;
    const controller = useAbortController(rootCategoryId);
    const country = useCurrentCountry();
    const { week, year } = useTikTokWeeks();
    const parentCat = useEntity({
        id: rootCategoryId,
        dataSource: DATA_SOURCES.GOOGLE,
    }).data;

    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);
    const metrics = getMetrics({ isNotTikTokMarket, trendType });
    const sortOptions = [
        ...Object.values(GOOGLE_SORT_OPTIONS),
        ...Object.values(TIKTOK_SORT_OPTIONS),
        ...FLAVORS_SORT_OPTIONS,
    ].filter(({ sortBy }) => metrics.some(([{ key }]) => key === sortBy));
    const defaultInitialSortOption = sortOptions.find(
        ({ sortBy }) => sortBy === GOOGLE_METADATA_BY_KEY.volume.key,
    );

    const trendIds =
        categoryQuery.data && categoryQuery.data[0]
            ? categoryQuery.data[0][trendType]
            : null;
    const trendDimension = trendType === 'cat_fives' ? 'categories' : trendType;

    const trendsQuery = useGoogleData({
        enabled: !!(trendIds && country),
        data: {
            [trendDimension]: trendIds,
            ...(trendType !== 'cat_fives' && {
                categories: [rootCategoryId],
            }),
            countries: country ? [country.id] : null,
        },
        paginateBy: trendDimension,
        includeNoDataItems: false,
        controller,
    });

    const name =
        categoryQuery.data && categoryQuery.data[0]
            ? categoryQuery.data[0].name
            : rootCategoryId
              ? 'index'
              : null;
    useEffect(() => {
        if (name && country) {
            const eventName =
                trendType === 'brands' ? 'Brand Trends' : 'Category Trends';

            mixpanelTrack(eventName, { name, countryName: country.name });
        }
    }, [name, country, trendType]);

    const blocker = googleExploreBlocker({
        rootCategoryId,
        parentCat,
        country,
    });
    if (blocker === 'industries') return <IndustriesPlaceholderMessage />;
    if (blocker === 'coverage')
        return <NoDataSourceMarketCoverage dataSource={DATA_SOURCES.GOOGLE} />;

    return (
        <ExploreDataSourceTable
            isLoading={
                categoryQuery.isFetching ||
                trendsQuery.isFetching ||
                (trendsQuery.data &&
                    trendsQuery.data.some(
                        ({ fetching, fetchingData }) =>
                            fetching || fetchingData,
                    ))
            }
            dataQuery={trendsQuery}
            category={parentCat}
            categoryQuery={categoryQuery}
            categoryId={rootCategoryId}
            country={country}
            {...{ [trendDimension]: trendIds }}
            week={week}
            year={year}
            trendDimension={trendDimension}
            paginateBy={trendDimension}
            name={parentCat ? parentCat.name : null}
            tableName={`explore-google-${type}-${isNotTikTokMarket}`}
            healthStatFilterOptions={getHealthStatFilterOptions({
                isNotTikTokMarket,
                trendType,
            })}
            getDownloadOptions={getGoogleDownloadOptions}
            source="google"
            mixpanelMeta={{ contentKey: 'CardsWrapper' }}
            filterSelectMenus={['cluster', 'healthStats']}
            defaultInitialSortOption={defaultInitialSortOption}
            PresetFilterChipsComponent={GooglePresetFilterChips}
            DrawerComponent={Drawer}
            DrawerContentDefaultComponent={GoogleExploreDrawerContent}
            sortOptions={sortOptions}
            metrics={metrics}
            rowHeight={GOOGLE_ROW_HEIGHT}
            noDataMessageOf={() =>
                googleNoDataMessage({
                    rootCategoryId,
                    trendsData: trendsQuery.data,
                    categoryData: categoryQuery.data?.[0],
                    type,
                })
            }
        />
    );
}

export const PageHeading = styled.h1`
    font-weight: ${({ theme }) => theme.fontBold};
    text-transform: capitalize;
    font-family: Montserrat;
    font-size: 18px;
    line-height: 22px;
    color: ${({ theme }) => theme.gray_1};
    margin-top: 0px;
`;

export const PageSubHeading = styled.h2`
    font-weight: normal;
    font-size: 16px;
    line-height: 20px;
    color: ${({ theme }) => theme.gray_3};
    margin: 0px;
`;

function CategoryTrends() {
    const location = useLocation();
    const { path } = useRouteMatch();
    const { type, categoryId } = useParams();
    const { mutateToWithCountryParam } = useCountryParam();
    const country = useCurrentCountry();
    const categoryQuery = useGoogleData({
        enabled: !!(categoryId && country),
        data: {
            categories: [categoryId],
            countries: country ? [country.id] : null,
        },
        paginateBy: 'categories',
        minimal: false,
    });
    const matchesGoogle = useRouteMatch({
        path: '/explore/google/(trends|brands)/:categoryId?',
    });
    const maybeCategoryIdForTikTok = matchesGoogle?.params?.categoryId;
    const toTikTok = mutateToWithCountryParam(
        `/explore/tiktok/${type}${maybeCategoryIdForTikTok ? `/${maybeCategoryIdForTikTok}` : ''}`,
    );
    const toOverview = mutateToWithCountryParam(
        `/explore/${type}${maybeCategoryIdForTikTok ? `/${maybeCategoryIdForTikTok}` : ''}`,
    );
    const generateLocation = ({ id }) => ({
        pathname: generatePath(path, { type, categoryId: id }),
        search: location.search,
    });
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);

    const parentCat = useEntity({
        id: categoryId,
        dataSource: DATA_SOURCES.GOOGLE,
    }).data;

    return (
        <PagePadding className="max-w-screen-2xl">
            <PageBar />
            <Stack $space="1.5rem">
                <div
                    css={`
                        position: relative;
                        z-index: 1;
                    `}
                >
                    <DataSourceBreadCrumbs
                        dataSource="GOOGLE"
                        cat1={
                            categoryQuery?.data?.[0]?.hierarchies?.[0]?.[1]
                                ?.cat1
                        }
                        geo={country?.text_id}
                        showIndustriesMenu={true}
                        generateLocation={generateLocation}
                        maxDepth={4}
                        replace={true}
                    />
                </div>
                <DataSourceTabsByCoverage
                    entityId={maybeCategoryIdForTikTok}
                    routePrefix="/explore"
                    hierarchyHint={
                        parentCat?.hierarchies?.[0]?.[1] ||
                        categoryQuery?.data?.[0]?.hierarchies?.[0]?.[1]
                    }
                    customMode={true}
                    customOverviewTo={toOverview}
                    customGoogleTo={location}
                    customTikTokTo={toTikTok}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/${type}${maybeCategoryIdForTikTok ? `/${maybeCategoryIdForTikTok}` : ''}`,
                    )}
                />
                <Category categoryQuery={categoryQuery} />
            </Stack>
        </PagePadding>
    );
}

export default CategoryTrends;
